// Cartoon character in the real world's draft as the form keeps it (spec
// G14-cartoon-in-the-real-world, PR d): the server holds the member's draft
// (`.../templates/cartoon-character-in-the-real-world/draft`), so it survives the panel; the form
// reads it, keeps its edits there, and asks the server's pack reader, director and stills. As Flat
// cartoon's (`flat-cartoon-draft.ts`), with eleven lines and shots, sixteen stills and the
// narrator's voice the member picks (Cartoon-10).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { z } from "zod";

import { ApiError } from "../../../api/errors";
import {
  getGetCartoonCharacterInTheRealWorldDraftQueryKey,
  useApproveCartoonCharacterInTheRealWorldScript,
  useGetCartoonCharacterInTheRealWorldDraft,
  useRegenerateCartoonCharacterInTheRealWorldStill,
  useResetCartoonCharacterInTheRealWorldDraft,
  useWriteCartoonCharacterInTheRealWorldDraft,
  useWriteCartoonCharacterInTheRealWorldScript,
  writeCartoonCharacterInTheRealWorldDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  CartoonDraft,
  CartoonDraftStill,
  CartoonScript,
  CartoonSpeaker,
  CartoonStillName,
  CartoonStills,
} from "../../../api/generated/model";
import {
  CartoonApproval,
  CartoonDraftWrite,
  CartoonInputs,
  CartoonScriptAsk,
  type CartoonDraftWriteOutput,
  type CartoonInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface CartoonForm {
  inputs: CartoonInputsOutput;
  script: CartoonScript | null;
}

/** The stills in display order (`cartoon_stills.ORDER`): the sheets and plates, then a still a shot. */
export const SHOTS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8", "s9", "s10", "s11"] as const;
export const STILL_ORDER: readonly CartoonStillName[] = [
  "creature_sheet",
  "scale_plate",
  "woman_sheet",
  "room_master",
  "product_sheet",
  ...SHOTS,
];

/** The eleven lines' speakers in the shots' order (the playbook's L1561-1571). */
export const SPEAKERS: readonly CartoonSpeaker[] = [
  "CREATURE",
  "CREATURE",
  "CREATURE",
  "NARRATOR",
  "NARRATOR",
  "CREATURE",
  "CREATURE",
  "WOMAN",
  "NARRATOR",
  "CREATURE",
  "NARRATOR",
];

type Cast = "creature" | "woman" | "product";
/** Who is in each shot, S1 to S11 (`cartoon_stills.CAST`, the playbook's L1590-1600). */
const CAST: readonly (readonly Cast[])[] = [
  ["creature"],
  ["creature"],
  ["creature", "woman"],
  ["creature", "woman", "product"],
  ["creature", "product"],
  ["creature", "product"],
  ["creature", "product"],
  ["woman", "product"],
  ["woman", "product"],
  ["creature", "product"],
  ["product"],
];
const SHEET_OF: Readonly<Record<Cast, CartoonStillName>> = {
  creature: "creature_sheet",
  woman: "woman_sheet",
  product: "product_sheet",
};

/** What each still is drawn from among the stills (`cartoon_stills.GRAPH`): the plate from the
 * creature's sheet; a shot from the room master, the sheet of each one in frame and the shot
 * before. The packshot is the draft's pick, no still's. */
export const STILL_SOURCES: Readonly<Partial<Record<CartoonStillName, readonly CartoonStillName[]>>> = {
  scale_plate: ["creature_sheet"],
  ...Object.fromEntries(
    SHOTS.map((shot, at) => [
      shot,
      [
        "room_master",
        ...(CAST[at] ?? []).map((who) => SHEET_OF[who]),
        ...(at > 0 ? [SHOTS[at - 1]] : []),
      ] as CartoonStillName[],
    ]),
  ),
};

/** One problem of the generated schema, in our words. */
function problemWords(issue: z.core.$ZodIssue): string {
  if (issue.code === "too_big" && issue.origin === "string") return `At most ${issue.maximum} characters.`;
  if (issue.code === "too_small" && issue.origin === "string") return "Fill this in.";
  return "This value is not valid.";
}

/**
 * The script's first problem on each of its fields (`lines` and `shots` for any line or shot), as
 * Approve's body checks it: an edit the schema refuses shows on its field, and Approve says why it
 * waits.
 */
export function scriptProblems(form: CartoonForm): Partial<Record<keyof CartoonScript, string>> {
  if (form.script === null) return {};
  const body = CartoonApproval.safeParse({ inputs: form.inputs, script: form.script });
  const problems: Partial<Record<keyof CartoonScript, string>> = {};
  if (body.success) return problems;
  for (const issue of body.error.issues) {
    const [root, field] = issue.path;
    if (root !== "script" || typeof field !== "string" || !(field in form.script)) continue;
    problems[field as keyof CartoonScript] ??= problemWords(issue);
  }
  return problems;
}

/** A script for the member to write: the eleven lines on their speakers and the eleven shots,
 * every text empty. */
export function blankScript(): CartoonScript {
  return {
    lines: SPEAKERS.map((speaker) => ({ speaker, text: "" })),
    shots: SHOTS.map(() => ({ action: "", camera: "" })),
    creature_name: "",
    woman_name: "",
    product_short: "",
    mixed_media_lock: "",
    creature_lock: "",
    woman_lock: "",
    product_lock: "",
    location_lock: "",
    clean_lock: "",
    creature_voice: "",
    super_name: "",
    super_line: "",
    pronunciations: [],
  };
}

/** The video's settings the footer keeps open once the script is approved: the form's own until
 * "Generate video" sends them. */
export interface VideoSettings {
  subtitles: boolean;
  resolution: CartoonInputsOutput["resolution"];
}

/** The fifteen stills the generations are drawn from, by name, once every still is done (the
 * scale plate is no generation's); null before. */
export function drawnStills(draft: CartoonDraft): CartoonStills | null {
  if (draft.stage !== "cast" || draft.stills.length === 0) return null;
  const drawn = Object.fromEntries(
    draft.stills.flatMap((each) => (each.output ? [[each.name, each.output] as const] : [])),
  );
  if (draft.stills.some((each) => !(each.name in drawn))) return null;
  const { s1, s2, s3, s4, s5, s6, s7, s8, s9, s10, s11 } = drawn;
  const { creature_sheet, woman_sheet, product_sheet, room_master } = drawn;
  if (!s1 || !s2 || !s3 || !s4 || !s5 || !s6 || !s7 || !s8 || !s9 || !s10 || !s11) return null;
  if (!creature_sheet || !woman_sheet || !product_sheet || !room_master) return null;
  return { s1, s2, s3, s4, s5, s6, s7, s8, s9, s10, s11, creature_sheet, woman_sheet, product_sheet, room_master };
}

/** "Generate video"'s body: the approved script, the stills by reference, the narrator's voice
 * and the footer's settings; null until every still is done and a voice is picked. */
export function videoBody(draft: CartoonDraft, settings: VideoSettings, narratorVoiceId: string | null) {
  if (draft.script === null || narratorVoiceId === null) return null;
  const stills = drawnStills(draft);
  if (stills === null) return null;
  return {
    type: "video.template" as const,
    inputs: {
      template: "cartoon-character-in-the-real-world" as const,
      script: draft.script,
      stills,
      narrator_voice_id: narratorVoiceId,
      ...settings,
    },
  };
}

/** The draft refetched while it works: every 2 s while a director or a still works, and a moment
 * after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** A still under way: drawing, or pending on stills each done or still coming. */
export function stillWaiting(name: CartoonStillName, stills: readonly CartoonDraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const sources = STILL_SOURCES[name] ?? [];
  return (
    sources.length > 0 &&
    sources.every(
      (source) => stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills),
    )
  );
}

/** Where the draft's work stands, as the progress line names it. */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  drawing: boolean;
}

export function workOf(draft: CartoonDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    reading: draft.pack_director === "working",
    writing: draft.script_director === "working" || (answering && draft.pack_director !== "working"),
    drawing: draft.stills.some((each) => stillWaiting(each.name, draft.stills)),
  };
}

function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: CartoonDraft): CartoonForm {
  const inputs = CartoonInputs.safeParse(draft.inputs);
  return { inputs: inputs.success ? inputs.data : CartoonInputs.parse({}), script: draft.script };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses; each ask
 * answers with the draft, shown from then on.
 */
export function useCartoonDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetCartoonCharacterInTheRealWorldDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<CartoonForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetCartoonCharacterInTheRealWorldDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll: number | false = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: CartoonDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteCartoonCharacterInTheRealWorldScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveCartoonCharacterInTheRealWorldScript(asks);
  const regeneration = useRegenerateCartoonCharacterInTheRealWorldStill(asks);
  const reset = useResetCartoonCharacterInTheRealWorldDraft(asks);
  const asking = script.isPending || approval.isPending || regeneration.isPending || reset.isPending;
  const work: DraftWork =
    draft === undefined ? { reading: false, writing: false, drawing: false } : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteCartoonCharacterInTheRealWorldDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: CartoonDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writeCartoonCharacterInTheRealWorldDraft(pending.teamId, pending.brandId, pending.data).catch(
          () => undefined,
        );
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = CartoonDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    editable,
    failure,
    approving: approval.isPending,
    resetting: reset.isPending,
    edit(change: Partial<CartoonForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<CartoonInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = CartoonScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = CartoonApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate(name: CartoonStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
